import * as THREE from "three";

/**
 * Compute triangle count + signed tet volume for a BufferGeometry.
 * Volume returned in the SAME cubic units as the geometry (mm³ for standard STL).
 */
export function geometryStats(geometry) {
  if (!geometry || !geometry.attributes?.position) {
    return { triangles: 0, volumeMm3: 0 };
  }
  const pos = geometry.attributes.position;
  const idx = geometry.index;
  let volume = 0;
  let triangles = 0;
  const v1 = new THREE.Vector3();
  const v2 = new THREE.Vector3();
  const v3 = new THREE.Vector3();

  if (idx) {
    for (let i = 0; i < idx.count; i += 3) {
      v1.fromBufferAttribute(pos, idx.getX(i));
      v2.fromBufferAttribute(pos, idx.getX(i + 1));
      v3.fromBufferAttribute(pos, idx.getX(i + 2));
      volume += signedTet(v1, v2, v3);
      triangles++;
    }
  } else {
    for (let i = 0; i < pos.count; i += 3) {
      v1.fromBufferAttribute(pos, i);
      v2.fromBufferAttribute(pos, i + 1);
      v3.fromBufferAttribute(pos, i + 2);
      volume += signedTet(v1, v2, v3);
      triangles++;
    }
  }
  return { triangles, volumeMm3: Math.abs(volume) };
}

function signedTet(a, b, c) {
  return (a.x * (b.y * c.z - b.z * c.y) + a.y * (b.z * c.x - b.x * c.z) + a.z * (b.x * c.y - b.y * c.x)) / 6;
}

/**
 * Generate a smart AI-style preliminary analysis from parsed metadata.
 * Designed as a placeholder / heuristic — structure kept stable so a future
 * real geometry analyzer can populate the same fields.
 */
export function buildAnalysis({ ext, size, triangles, volumeMm3, fileName, fileSize }) {
  const out = {
    fileType: ext || "—",
    printability: "İncelemede",
    printabilityTone: "neutral", // good | neutral | warn
    method: "—",
    materials: "—",
    risks: "—",
    nextStep: "Teklif formunu gönderin, teknik ekip dosyanızı inceleyip size dönüş sağlasın.",
    meta: {
      fileName: fileName || "—",
      fileSize: fileSize ? formatBytes(fileSize) : "—",
      dimensions: size ? `${fmt(size.x)} × ${fmt(size.y)} × ${fmt(size.z)} mm` : null,
      triangles: triangles ? triangles.toLocaleString("tr-TR") : null,
      volumeCm3: volumeMm3 ? (volumeMm3 / 1000).toFixed(2) : null,
      orientation: size ? suggestOrientation(size) : null,
    },
  };

  if (ext === "STL" || ext === "OBJ") {
    out.printability = "Uygun görünüyor";
    out.printabilityTone = "good";
    out.method = "FDM (varsayılan) — hassas detay için SLA";
    out.materials = "PLA / PETG • dayanım için ABS veya Nylon";

    if (size) {
      const maxDim = Math.max(size.x, size.y, size.z);
      if (maxDim > 300) {
        out.risks = "Parça büyük boyutlu: parçalı üretim veya büyük hacim yatak gerekebilir.";
      } else if (maxDim < 5) {
        out.risks = "Çok küçük ölçek: SLA reçine tercih edilmeli, detay kaybı riski.";
        out.method = "SLA / DLP (yüksek çözünürlük)";
      } else {
        out.risks = "İnce duvarlar veya destek gereksinimi üretim öncesi kontrol edilmelidir.";
      }
    } else {
      out.risks = "İnce duvarlar veya destek gereksinimi üretim öncesi kontrol edilmelidir.";
    }

    if (triangles && triangles > 1_000_000) {
      out.risks += " Mesh yoğunluğu yüksek — remesh optimizasyonu önerilebilir.";
    }
  } else if (ext === "STEP") {
    out.printability = "STEP alındı • önizleme STL/OBJ için desteklenir";
    out.printabilityTone = "neutral";
    out.method = "Mühendislik inceleme sonrası belirlenir";
    out.materials = "Uygulama alanına göre önerilecek";
    out.risks = "STEP parametrik format — nihai değerlendirme teknik ekibimizce yapılacak.";
  } else if (ext === "3MF") {
    out.printability = "Uygun görünüyor";
    out.printabilityTone = "good";
    out.method = "FDM (varsayılan) — hassas detay için SLA";
    out.materials = "PLA / PETG • dayanım için ABS veya Nylon";
    out.risks = "3MF tarayıcıda önizlenmiyor; sunucu taraflı geometri analizi yapılıyor.";
  }
  return out;
}

function fmt(n) { return n ? n.toFixed(1) : "0.0"; }
function formatBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function suggestOrientation(size) {
  // suggest flattest axis as print bed
  const axes = [
    { name: "XY tabanı (Z yukarı)", v: size.z },
    { name: "XZ tabanı (Y yukarı)", v: size.y },
    { name: "YZ tabanı (X yukarı)", v: size.x },
  ];
  axes.sort((a, b) => a.v - b.v);
  return axes[0].name;
}

/* ============================================================
 * PRICE ESTIMATE HEURISTIC
 * Ballpark — not a guaranteed quote. Kept intentionally simple
 * so a real pricing engine can replace it later.
 * ============================================================ */

// TL per cm³ [low, high] including labor + post-processing band.
const MATERIAL_RATES = {
  PLA: [12, 22],
  PETG: [15, 28],
  ABS: [18, 32],
  RESIN: [45, 90],
  NYLON: [60, 120],
  TPU: [25, 48],
};

const SETUP_FEE = 150; // TL, minimum handling / slicing / QA
const MIN_PRICE_TL = 250; // below this we round up (not worth the run)

/**
 * Estimate a price range (TL) for a single quote.
 * @param {Object} args
 * @param {string} args.material
 * @param {number} args.quantity
 * @param {Object|null} args.rawMeta
 * @param {Object} [args.config] - dynamic pricing config from `/api/public/settings`
 *                                  { setup_fee, min_price_tl, material_rates, bulk_tiers, fallback_rate }
 */
export function estimatePrice({ material, quantity, rawMeta, config }) {
  const qty = Math.max(1, parseInt(quantity) || 1);
  const cfg = config || {};
  const setupFee = cfg.setup_fee ?? SETUP_FEE;
  const minPrice = cfg.min_price_tl ?? MIN_PRICE_TL;
  const rates = cfg.material_rates || MATERIAL_RATES;
  const fallback = cfg.fallback_rate || [12, 90];
  const tiers = (cfg.bulk_tiers && cfg.bulk_tiers.length ? cfg.bulk_tiers : [
    { qty: 3, factor: 0.92 }, { qty: 5, factor: 0.86 }, { qty: 10, factor: 0.78 },
  ]).slice().sort((a, b) => a.qty - b.qty);

  if (!rawMeta) return { status: "fallback", reason: "no-file" };
  if (rawMeta.ext === "STEP") return { status: "fallback", reason: "step" };
  const hasVolume = !!rawMeta.volumeMm3 && rawMeta.volumeMm3 > 0;
  const hasSize = rawMeta.size && (rawMeta.size.x || rawMeta.size.y || rawMeta.size.z);
  if (!hasVolume && !hasSize) return { status: "fallback", reason: "no-metrics" };

  let volumeCm3;
  let confidence = "medium";
  if (hasVolume) { volumeCm3 = rawMeta.volumeMm3 / 1000; confidence = "high"; }
  else {
    const s = rawMeta.size;
    volumeCm3 = ((s.x * s.y * s.z) / 1000) * 0.35;
    confidence = "low";
  }
  if (volumeCm3 < 0.5) volumeCm3 = 0.5;

  const rate = rates[material] || fallback;

  let low = volumeCm3 * rate[0] + setupFee;
  let high = volumeCm3 * rate[1] + setupFee;

  // bulk factor — pick the highest qty tier whose qty <= user qty
  let bulkFactor = 1.0;
  for (const t of tiers) if (qty >= t.qty) bulkFactor = t.factor;
  low *= qty * bulkFactor;
  high *= qty * bulkFactor;

  if (rawMeta.triangles && rawMeta.triangles > 500_000) { low += 150; high += 350; }

  low = Math.max(low, minPrice);
  high = Math.max(high, low * 1.25);
  low = roundToBand(low);
  high = roundToBand(high);

  return { status: "ready", low, high, confidence, volumeCm3, qty, bulkFactor, material };
}

function roundToBand(n) {
  if (n < 500) return Math.ceil(n / 10) * 10;
  if (n < 2000) return Math.ceil(n / 25) * 25;
  if (n < 10000) return Math.ceil(n / 50) * 50;
  return Math.ceil(n / 100) * 100;
}

export function formatTRY(n) {
  if (n == null) return "—";
  try {
    return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n);
  } catch {
    return `₺${Math.round(n).toLocaleString("tr-TR")}`;
  }
}
